import type { EChartsOption } from 'echarts'
import type { TimeSeriesSeries } from './types'

export type ChartThemeColors = {
  fg: string
  mutedFg: string
  border: string
  card: string
  tooltipBg: string
  tooltipBorder: string
}

const EMPTY_THEME: ChartThemeColors = {
  fg: '#111928',
  mutedFg: '#6B7280',
  border: '#D1D5DB',
  card: '#ffffff',
  tooltipBg: '#ffffff',
  tooltipBorder: '#D1D5DB',
}

/** Resolve a Vanilla Extract `var(--…)` token against an element. */
export function resolveCssColor(cssVarRef: string, el: Element | null): string {
  if (!el || typeof window === 'undefined') return cssVarRef
  const match = cssVarRef.match(/var\((--[^),]+)/)
  if (!match) return cssVarRef
  const value = getComputedStyle(el).getPropertyValue(match[1]).trim()
  return value || cssVarRef
}

export function readChartTheme(el: Element | null, tokens: {
  fg: string
  mutedFg: string
  border: string
  card: string
}): ChartThemeColors {
  if (!el) return EMPTY_THEME
  const fg = resolveCssColor(tokens.fg, el)
  const mutedFg = resolveCssColor(tokens.mutedFg, el)
  const border = resolveCssColor(tokens.border, el)
  const card = resolveCssColor(tokens.card, el)
  return {
    fg,
    mutedFg,
    border,
    card,
    tooltipBg: card,
    tooltipBorder: border,
  }
}

function uniqueUnits(series: TimeSeriesSeries[]): string[] {
  // Axis order from series yAxisIndex; prefer power label on left and % on right when multiple share an axis
  const leftUnits = series.filter((s) => s.yAxisIndex === 0).map((s) => s.unit.trim()).filter(Boolean)
  const rightUnits = series.filter((s) => s.yAxisIndex === 1).map((s) => s.unit.trim()).filter(Boolean)

  const pickPower = (units: string[]) =>
    units.find((u) => {
      const n = u.toLowerCase().replace(/\s+/g, '')
      return n === 'kw'
    }) ??
    units.find((u) => {
      const n = u.toLowerCase().replace(/\s+/g, '')
      return n === 'w' || n === 'mw'
    }) ??
    units[0]

  const pickPercent = (units: string[]) =>
    units.find((u) => isPercentAxisUnit(u)) ?? units[0]

  const units: string[] = []
  if (leftUnits.length) units.push(pickPower(leftUnits))
  if (rightUnits.length) {
    const right = pickPercent(rightUnits)
    if (right && right !== units[0]) units.push(right)
  }
  if (units.length > 0) return units.slice(0, 2)

  for (const s of series) {
    const u = s.unit.trim() || ''
    if (u && !units.includes(u)) units.push(u)
  }
  return units.slice(0, 2)
}

function isPercentAxisUnit(unit: string): boolean {
  const n = unit.trim().toLowerCase().replace(/\s+/g, '')
  return n === '%' || n === 'percent' || n === 'pct'
}

function formatAxisLabel(value: number): string {
  if (!Number.isFinite(value)) return ''
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 10_000) return `${(value / 1_000).toFixed(1)}k`
  if (abs >= 100) return value.toFixed(0)
  if (abs >= 10) return value.toFixed(1)
  return value.toFixed(2)
}

/**
 * Format an X-axis tick for a time domain.
 * ECharts' default time labels switch to day-of-month at day boundaries
 * (e.g. "5" / "6" on a Today range) — always prefer clock time with AM/PM.
 */
export function formatTimeAxisTick(
  value: number,
  opts: { hour12?: boolean; domainMs?: number } = {},
): string {
  if (!Number.isFinite(value)) return ''
  const hour12 = opts.hour12 !== false
  const d = new Date(value)
  const span = opts.domainMs ?? 24 * 60 * 60 * 1000

  // Multi-day ranges: include the date so midnight boundaries stay clear
  if (span > 36 * 60 * 60 * 1000) {
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      hour12,
    })
  }

  // Short windows: include minutes
  if (span <= 2 * 60 * 60 * 1000) {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12,
    })
  }

  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    hour12,
  })
}

function minIntervalForDomain(domainMs: number | undefined): number | undefined {
  if (domainMs == null || !Number.isFinite(domainMs) || domainMs <= 0) return undefined
  if (domainMs <= 2 * 60 * 60 * 1000) return 15 * 60 * 1000
  if (domainMs <= 12 * 60 * 60 * 1000) return 60 * 60 * 1000
  // Day-ish windows: keep ~2h ticks so labels stay as "12 AM" not day-of-month crumbs
  return 2 * 60 * 60 * 1000
}

export function buildTimeSeriesOption(
  series: TimeSeriesSeries[],
  theme: ChartThemeColors,
  xDomain?: { min: number; max: number } | null,
  hour12 = true,
): EChartsOption {
  const units = uniqueUnits(series)
  const legendSelected: Record<string, boolean> = {}
  for (const s of series) {
    legendSelected[s.label] = s.visible !== false
  }

  const yAxis = units.map((unit, index) => {
    const percent = isPercentAxisUnit(unit)
    return {
      type: 'value' as const,
      name: unit || undefined,
      nameTextStyle: {
        color: theme.mutedFg,
        fontSize: 11,
        padding: [0, 0, 0, 0] as [number, number, number, number],
      },
      position: (index === 0 ? 'left' : 'right') as 'left' | 'right',
      alignTicks: !percent,
      min: percent ? 0 : undefined,
      max: percent ? 100 : undefined,
      axisLine: { show: true, lineStyle: { color: theme.border } },
      axisTick: { show: false },
      axisLabel: {
        color: theme.mutedFg,
        fontSize: 11,
        formatter: (v: number) => (percent ? `${v}` : formatAxisLabel(v)),
      },
      splitLine: {
        show: index === 0,
        lineStyle: { color: theme.border, type: 'dashed' as const, opacity: 0.6 },
      },
    }
  })

  // Ensure at least one y-axis so an empty chart still lays out cleanly
  if (yAxis.length === 0) {
    yAxis.push({
      type: 'value',
      name: undefined,
      nameTextStyle: { color: theme.mutedFg, fontSize: 11, padding: [0, 0, 0, 0] },
      position: 'left',
      alignTicks: true,
      min: undefined,
      max: undefined,
      axisLine: { show: true, lineStyle: { color: theme.border } },
      axisTick: { show: false },
      axisLabel: {
        color: theme.mutedFg,
        fontSize: 11,
        formatter: (v: number) => formatAxisLabel(v),
      },
      splitLine: {
        show: true,
        lineStyle: { color: theme.border, type: 'dashed', opacity: 0.6 },
      },
    })
  }

  return {
    animationDuration: 400,
    animationEasing: 'cubicOut',
    color: series.map((s) => s.color),
    grid: {
      left: 56,
      right: units.length > 1 ? 56 : 24,
      top: 28,
      bottom: series.length > 0 ? 56 : 32,
      containLabel: false,
    },
    legend: {
      type: 'scroll',
      bottom: 0,
      left: 'center',
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 8,
      textStyle: { color: theme.fg, fontSize: 12 },
      pageTextStyle: { color: theme.mutedFg },
      selected: legendSelected,
      data: series.map((s) => ({
        name: s.label,
        itemStyle: { color: s.color },
      })),
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: { color: theme.mutedFg, width: 1, type: 'solid', opacity: 0.5 },
      },
      backgroundColor: theme.tooltipBg,
      borderColor: theme.tooltipBorder,
      borderWidth: 1,
      textStyle: { color: theme.fg, fontSize: 12 },
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-radius: 8px;',
      formatter: (params: unknown) => {
        const items = Array.isArray(params) ? params : [params]
        if (items.length === 0) return ''
        const first = items[0] as { axisValue?: string | number; value?: unknown }
        const axisValue = first.axisValue
        const ts = typeof axisValue === 'number'
          ? axisValue
          : typeof axisValue === 'string'
            ? Number(axisValue)
            : NaN
        const header = Number.isFinite(ts)
          ? new Date(ts).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              second: '2-digit',
              hour12,
            })
          : String(axisValue ?? '')

        const lines = items.map((raw) => {
          const p = raw as {
            seriesName?: string
            color?: string
            data?: unknown
            value?: unknown
          }
          const data = p.data
          let y: number | null = null
          if (Array.isArray(data) && data.length >= 2) {
            const v = data[1]
            y = typeof v === 'number' ? v : v == null ? null : Number(v)
          } else if (typeof p.value === 'number') {
            y = p.value
          }
          const seriesMeta = series.find((s) => s.label === p.seriesName)
          const unit = seriesMeta?.unit ? ` ${seriesMeta.unit}` : ''
          const display = y == null || !Number.isFinite(y)
            ? '—'
            : `${y.toFixed(2)}${unit}`
          const swatch = `<span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${p.color ?? '#999'};margin-right:6px;"></span>`
          return `<div style="margin-top:4px;">${swatch}${p.seriesName ?? ''}: <strong>${display}</strong></div>`
        })
        return `<div style="font-weight:500;margin-bottom:2px;">${header}</div>${lines.join('')}`
      },
    },
    xAxis: {
      type: 'time',
      min: xDomain?.min,
      max: xDomain?.max,
      minInterval: minIntervalForDomain(
        xDomain != null ? xDomain.max - xDomain.min : undefined,
      ),
      axisLine: { lineStyle: { color: theme.border } },
      axisTick: { show: false },
      axisLabel: {
        color: theme.mutedFg,
        fontSize: 11,
        hideOverlap: true,
        showMinLabel: true,
        showMaxLabel: true,
        formatter: (value: number | string) => {
          const n = typeof value === 'number' ? value : Number(value)
          return formatTimeAxisTick(n, {
            hour12,
            domainMs: xDomain != null ? xDomain.max - xDomain.min : undefined,
          })
        },
      },
      splitLine: { show: false },
    },
    yAxis,
    series: series.map((s) => {
      const maxAxis = Math.max(0, yAxis.length - 1)
      const yAxisIndex = Math.min(Math.max(0, s.yAxisIndex), maxAxis)
      return {
        id: s.id,
        name: s.label,
        type: 'line' as const,
        yAxisIndex,
        showSymbol: false,
        smooth: 0.15,
        connectNulls: false,
        sampling: 'lttb' as const,
        lineStyle: { width: 2, color: s.color },
        itemStyle: { color: s.color },
        areaStyle: {
          color: s.color,
          opacity: 0.18,
        },
        emphasis: { focus: 'series' as const },
        data: s.points.map((p) => [p.x, p.y] as [number, number | null]),
      }
    }),
  }
}
